import CheckCircle from "@mui/icons-material/CheckCircle";
import CloseIcon from "@mui/icons-material/Close";
import Error from "@mui/icons-material/Error";
import Info from "@mui/icons-material/Info";
import Lock from "@mui/icons-material/Lock";
import Warning from "@mui/icons-material/Warning";
import type {
  SnackbarKey,
  SnackbarOrigin,
  SnackbarProviderProps,
  VariantType,
} from "notistack";
import { SnackbarProvider as NotistackProvider } from "notistack";
import type { ComponentType, ReactNode } from "react";
import { useCallback, useRef } from "react";

import * as sx from "./index.sx";
import { StructuredContent } from "./structured";

export { getPermissionSnackbarOptions } from "./permission";
export { getStructuredSnackbarOptions } from "./structured";

declare module "notistack" {
  interface VariantOverrides {
    permission: true;
    structured: {
      body?: ReactNode;
      icon?: ReactNode;
      title: ReactNode;
    };
  }
}

export const SNACKBAR_ICONS = {
  error: Error,
  info: Info,
  permission: Lock,
  success: CheckCircle,
  warning: Warning,
} as const satisfies Record<
  Exclude<VariantType, "default" | "structured">,
  ComponentType
>;
type Variant = keyof typeof SNACKBAR_ICONS;
export const VARIANTS = Object.keys(SNACKBAR_ICONS) as Variant[];

const iconVariant = Object.fromEntries(
  Object.entries(SNACKBAR_ICONS).map(([variant, Icon]) => [
    variant,
    /**
     * Add `data-testid` to snackbar icons for testing
     */
    <Icon key={variant} data-testid={`${variant} snackbar icon`} />,
  ]),
);

const snackbarOrigin: SnackbarOrigin = {
  horizontal: "right",
  vertical: "bottom",
};

const Components: Required<SnackbarProviderProps["Components"]> = {
  default: sx.StyledContent,
  error: sx.StyledContent,
  info: sx.StyledContent,
  permission: sx.StyledContent,
  structured: StructuredContent,
  success: sx.StyledContent,
  warning: sx.StyledContent,
};

export const SnackbarProvider = ({
  autoHideDuration = 3000,
  children,
}: {
  autoHideDuration?: number | null;
  children: React.ReactNode;
}) => {
  const notistackRef = useRef<NotistackProvider>(null);

  const action = useCallback(
    (key: SnackbarKey) => (
      <sx.CloseButton
        data-testid="close toast"
        size="small"
        onClick={() => {
          notistackRef.current!.closeSnackbar(key);
        }}
      >
        <CloseIcon fontSize="small" />
      </sx.CloseButton>
    ),
    [notistackRef],
  );

  return (
    <NotistackProvider
      anchorOrigin={snackbarOrigin}
      autoHideDuration={autoHideDuration}
      Components={Components}
      iconVariant={iconVariant}
      maxSnack={1}
      ref={notistackRef}
      action={action}
    >
      {children}
    </NotistackProvider>
  );
};
